import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  FabricImage,
  type FabricObject,
  filters,
  Group,
  Rect,
  type TMat2D,
} from 'fabric';
import {
  getFullArchPath,
  getToothMapIdentifier,
} from '@witmetrics/unison-core';
import type { Coords } from '@witmetrics/utils';
import { isEmpty } from '@witmetrics/utils/misc';
import { CONTROLS, useFabric } from '@/providers/FabricProvider';
import { useFaceLandmarks } from '@/providers/FaceLandmarksProvider';
import {
  cloneBoundingBox,
  createCornerControls,
  findObjectByImageType,
  findObjectByName,
  parseTransform,
} from '@/utils/fabric';
import { useToggle } from '@/hooks/useToggle';
import { canvasToBlob, createCanvas, createImage } from '@/utils/dom';
import { scaleImageToFit, trimTransparentEdges } from '@/utils/images';
import type { CanvasCapture } from '@/types/files';
import { useNumber } from '@/hooks/useNumber';
import { showErrorToaster } from '@/utils/toasters';
import Card from '@/components/Card';
import LoopingEllipsis from '@/components/LoopingEllipsis';
import Overlay from '@/components/Overlay';
import TitleBar from './TitleBar';
import ControlsHeader from './ControlsHeader';
import type { FaceFilterSettings, ImageMap, SimulationStep } from './types';
import { DEFAULT_SETTINGS, STEPS } from './constants';
import { classes, getMouthBoundingBox } from './utils';

const { SELECT_MOUTH, PLACE_TEETH } = STEPS;

export type SmileSimulationOverlayProps = {
  file: File;
  onSave: (result: File, compare: File) => void;
  onClose: () => void;
};

export default function SmileSimulationOverlay({
  file,
  onSave,
  onClose,
}: SmileSimulationOverlayProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageMap = useRef<ImageMap>(new Map());
  const { setCanvas, setActiveControl, getFabric, setPanning } = useFabric();
  const {
    getImageLandmarker,
    initializeImageLandmarker,
    detectImageLandmarks,
    getMouthOutlines,
  } = useFaceLandmarks();
  const [archSettings, setArchSettings] = useState(DEFAULT_SETTINGS);
  const [opacity, setOpacity] = useNumber(0);
  const [filter, setFilter] = useState<FaceFilterSettings>({
    brightness: 50,
    contrast: 50,
  });
  const [activeStep, setActiveStep] = useState<SimulationStep>(SELECT_MOUTH);
  const [isLoading, toggleLoading] = useToggle();
  const [isCaptureMode, toggleCaptureMode] = useToggle(); // TODO: This should probably be via useFabric() or something

  useEffect(() => {
    // NOTE: Basically just giving the canvasRef time to load
    if (!file || imageMap.current.has(file.name)) return;
    if (!isLoading) toggleLoading(true);
    else if (isLoading) {
      handleLoadCanvas();
      handleLoadFile();
    }
  }, [file, isLoading]);

  useLayoutEffect(() => {
    handleLoadArch();
  }, [archSettings]);

  useEffect(() => {
    handleUpdateOpacity();
  }, [opacity]);

  useEffect(() => {
    handleUpdateFilter();
  }, [filter]);

  const handleReset = () => {
    setActiveStep(SELECT_MOUTH);
    handleLoadFile();
  };

  const handleUpdateOpacity = () => {
    const fabric = getFabric();
    if (!fabric) return;
    const before = findObjectByImageType(fabric, 'BEFORE');
    if (before) {
      before.opacity = 1 - opacity / 100;
      fabric.renderAll();
    }
  };

  const handleUpdateFilter = () => {
    const fabric = getFabric();
    if (!fabric) return;
    const brightness = filter.brightness / 50 - 1;
    const contrast = filter.contrast / 50 - 1;
    const before = findObjectByImageType(fabric, 'BEFORE')! as FabricImage;
    const face = findObjectByImageType(fabric, 'FACE')! as FabricImage;
    [before, face].forEach((obj) => {
      obj.filters = [
        new filters.Brightness({ brightness }),
        new filters.Contrast({ contrast }),
      ];
      obj.applyFilters();
    });
    fabric.renderAll();
  };

  const handleLoadCanvas = () => {
    if (!canvasRef.current) return console.warn('Canvas ref is null');
    if (!getFabric()) setCanvas(canvasRef.current);
  };

  const handleLoadFile = async () => {
    const fabric = getFabric();
    if (!fabric) return console.warn('Fabric not initialized');
    fabric.clear();
    let img: HTMLImageElement;
    let landmarker = getImageLandmarker();
    if (landmarker) {
      img = await createImage(URL.createObjectURL(file));
    } else {
      [img, landmarker] = await Promise.all([
        createImage(URL.createObjectURL(file)),
        initializeImageLandmarker(),
      ]);
    }
    // NOTE: Limit image size to prevent WebGL MAX_TEXTURE_SIZE issues
    img = await scaleImageToFit(img, 1400, 1400);
    imageMap.current.set(file.name, img);
    const [mask, min, max] = await handleCreateMask(img);
    const [face, before] = await Promise.all([
      buildFabricImage(mask),
      buildFabricImage(img),
      handleLoadArch(),
    ]);
    face.imageType = 'FACE';
    before.imageType = 'BEFORE';
    face.selectable = false;
    before.selectable = false;
    before.opacity = 1 - opacity / 100;
    // Add a dark background behind the mouth
    const width = max[0] - min[0];
    const height = max[1] - min[1];
    const rect = new Rect();
    rect.name = 'MOUTH';
    rect.width = 1.5 * width;
    rect.height = 1.5 * height;
    rect.left = min[0] - 0.25 * width;
    rect.top = min[1] - 0.25 * height;
    rect.selectable = false;
    rect.fill = 'hsl(353, 45%, 20%)';
    const arch = findObjectByImageType(fabric, 'UPPER_ARCH');
    if (!arch) return; // TODO: Should be better error handling
    arch.scaleToWidth(width);
    arch.left = min[0];
    arch.top = min[1] - 0.2 * (arch.height * arch.scaleY); // NOTE: Slightly offsetting to keep above the gumline
    const archSelector = buildArchSelector(arch);
    fabric.clear();
    const group = new Group([rect, arch, face, before, archSelector]);
    scaleGroup(group);
    fabric.add(...group.removeAll());
    fabric.renderAll();
    setActiveControl(CONTROLS.PAN);
    setActiveStep(PLACE_TEETH);
    setOpacity(100);
    toggleLoading(false);
  };

  const handleCreateMask = async (
    img: HTMLImageElement
  ): Promise<[HTMLImageElement, Coords, Coords]> => {
    const landmarks = detectImageLandmarks(img);
    if (isEmpty(landmarks)) showErrorToaster('Unable to detect smile');
    const coords: Coords[] = getMouthOutlines(landmarks)[0].map(([x, y]) => [
      x * img.width,
      y * img.height,
    ]);
    const { canvas, ctx } = createCanvas(img.width, img.height);
    let minX = coords[0][0];
    let minY = coords[0][1];
    let maxX = coords[0][0];
    let maxY = coords[0][1];
    ctx.drawImage(img, 0, 0);
    ctx.save();
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'black';
    ctx.fillStyle = 'black';
    ctx.lineJoin = 'round';
    // 750,000 is a bit of an arbitrary threshold but seems alright
    ctx.filter = `blur(${img.width * img.height < 750000 ? 1 : 2}px)`;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.moveTo(coords[0][0], coords[0][1]);
    ctx.beginPath();
    for (let i = 1; i < coords.length; i++) {
      const [x, y] = coords[i];
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    const mask = await createImage(canvas.toDataURL('image/png'));
    return [mask, [minX, minY], [maxX, maxY]];
  };

  const scaleGroup = (group: Group) => {
    const fabric = getFabric();
    if (!fabric) return;
    let { width, height } = group.getBoundingRect();
    if (width > height) {
      // Hot dog style
      group.scaleToHeight(0.95 * fabric.height);
      if (group.width > 0.95 * fabric.width) {
        group.scaleToWidth(0.95 * fabric.width);
      }
    } else {
      // Hamburger style
      group.scaleToWidth(0.95 * fabric.width);
      if (group.height > 0.95 * fabric.height) {
        group.scaleToHeight(0.95 * fabric.height);
      }
    }
    height = group.scaleY * group.height;
    width = group.scaleX * group.width;
    group.left = 0.5 * (fabric.width - width);
    group.top = 0.5 * (fabric.height - height);
  };

  const buildFabricImage = async (img: HTMLImageElement) => {
    const fabricImage = await FabricImage.fromURL(img.src);
    if (!fabricImage) throw Error('FabricImage is null'); // TODO: Not sure why it can possibly be null
    fabricImage.lockScalingFlip = true;
    fabricImage.lockSkewingX = true;
    fabricImage.lockSkewingY = true;
    fabricImage.controls = createCornerControls();
    return fabricImage;
  };

  const handleLoadArch = async () => {
    const fabric = getFabric();
    if (!fabric) return;
    const key = getToothMapIdentifier(archSettings, 'full');
    const path = getFullArchPath(archSettings);
    let img = imageMap.current.get(key);
    if (!img) {
      img = await createImage(path);
      imageMap.current.set(key, img);
    }
    const arch = await buildFabricImage(img);
    const oldArch = findObjectByImageType(fabric, 'UPPER_ARCH');
    if (oldArch) {
      cloneBoundingBox(arch, oldArch);
      fabric.remove(oldArch);
    }
    arch.name = key;
    arch.imageType = 'UPPER_ARCH';
    const archSelector = buildArchSelector(arch);
    // Make sure everything is in the right order
    const before = findObjectByImageType(fabric, 'BEFORE');
    const face = findObjectByImageType(fabric, 'FACE');
    const rect = findObjectByName(fabric, 'MOUTH');
    let objects: FabricObject[] = [arch];
    if (rect) objects.unshift(rect);
    if (face) objects.push(face);
    if (before) objects.push(before);
    fabric.clear();
    fabric.add(...objects, archSelector);
    fabric.renderAll();
  };

  const buildArchSelector = (arch: FabricObject) => {
    const rect = new Rect();
    rect.name = 'ARCH_SELECTOR';
    rect.selectable = true;
    rect.fill = 'transparent';
    rect.width = arch.width;
    rect.height = arch.height;
    cloneBoundingBox(rect, arch);
    rect.on('selected', () => setPanning(false));
    rect.on('deselected', () => setPanning(true));
    rect.on('moving', () => cloneBoundingBox(arch, rect));
    rect.on('scaling', () => cloneBoundingBox(arch, rect));
    rect.on('rotating', () => cloneBoundingBox(arch, rect));
    return rect;
  };

  const captureCanvas = async (): Promise<HTMLImageElement> => {
    return createImage(
      URL.createObjectURL(await canvasToBlob(getFabric()!.lowerCanvasEl))
    );
  };

  const captureFace = async (): Promise<HTMLImageElement> => {
    const fabric = getFabric()!;
    const { objects } = getMouthBoundingBox(fabric);
    const face = findObjectByImageType(fabric, 'BEFORE')!;
    face.visible = true;
    objects.forEach((obj) => (obj.visible = false));
    fabric.renderAll();
    return captureCanvas();
  };

  const captureMask = async (): Promise<HTMLImageElement> => {
    const fabric = getFabric()!;
    const { objects } = getMouthBoundingBox(fabric);
    const face = findObjectByImageType(fabric, 'BEFORE')!;
    face.visible = false;
    objects.forEach((obj) => {
      obj.visible = true;
    });
    fabric.renderAll();
    return captureCanvas();
  };

  const buildMask = async (capture: CanvasCapture) => {
    const fabric = getFabric()!;
    const mask = await FabricImage.fromURL(URL.createObjectURL(capture.blob));
    const { scale, left, top } = parseTransform(fabric.viewportTransform);
    mask.width = capture.width;
    mask.height = capture.height;
    mask.scale(1 / scale);
    mask.left = (capture.left - left) / scale;
    mask.top = (capture.top - top) / scale;
    mask.selectable = false;
    mask.name = 'MASK';
    return mask;
  };

  const handleSaveMask = async (capture: CanvasCapture) => {
    // NOTE: `capture` is the result from MouthSelectionViewer
    toggleCaptureMode(false);
    setActiveControl(CONTROLS.PAN);
    const fabric = getFabric();
    if (!fabric) return;
    const transform: TMat2D = [...fabric.viewportTransform]; // Stash the current transform
    const mask = await buildMask(capture);
    fabric.add(mask);
    fabric.setViewportTransform([1, 0, 0, 1, 0, 0]); // Reset to initial transform
    // Convert face and mask to images
    const faceImg = await captureFace();
    const maskImg = await captureMask();
    // Merge the two images
    const { canvas, ctx } = createCanvas(fabric.width, fabric.height);
    ctx.drawImage(faceImg, 0, 0);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(maskImg, 0, 0);
    canvas.style.backgroundColor = 'transparent';
    const img = await createImage(
      URL.createObjectURL(await canvasToBlob(canvas))
    );
    const before = findObjectByImageType(fabric, 'BEFORE')!;
    before.visible = true;
    const trimmedImg = await createImage(
      URL.createObjectURL((await trimTransparentEdges(img)).blob)
    );
    const face = await buildFabricImage(trimmedImg);
    face.imageType = 'FACE';
    face.selectable = false;
    face.left = before.left;
    face.top = before.top;
    face.scaleToWidth(before.width * before.scaleX);
    // Restore the existing pan/zoom settings
    fabric.setViewportTransform(transform);
    const { scale, left, top } = parseTransform(fabric.viewportTransform);
    // Add a dark background behind the mouth
    const rect = new Rect();
    rect.name = 'MOUTH';
    rect.width = capture.width;
    rect.height = capture.height;
    rect.scale(1 / scale);
    rect.left = (capture.left - left) / scale;
    rect.top = (capture.top - top) / scale;
    rect.selectable = false;
    rect.fill = 'hsl(353, 45%, 20%)';
    // fabric.add(rect);

    // Add the initial arch behind the masked image
    await handleLoadArch();
    const arch = findObjectByImageType(fabric, 'UPPER_ARCH');
    if (!arch) return;
    arch.scaleToWidth(mask.width);
    arch.left = mask.left;
    arch.top = mask.top - 5; // NOTE: Slightly offsetting to keep above the gumline
    const archSelector = buildArchSelector(arch);
    fabric.remove(mask);
    fabric.clear();
    fabric.add(rect, arch, face, before, archSelector);
    fabric.renderAll();
    setActiveControl(CONTROLS.PAN);
    setActiveStep(PLACE_TEETH);
    setOpacity(100);
  };

  const handleSave = async () => {
    const fabric = getFabric();
    if (!fabric) return;
    const transform: TMat2D = [...fabric.viewportTransform]; // Stash the current transform
    fabric.setViewportTransform([1, 0, 0, 1, 0, 0]); // Reset to initial transform
    fabric.renderAll();
    // Convert the canvas to a blob and create a file (result.png)
    const blob = (await trimTransparentEdges(await captureCanvas())).blob;
    const result = new File([blob], `result.png`, { type: blob.type });
    // Render the before and after images side by side (compare.png)
    const before = imageMap.current.get(file.name)!;
    const after = await createImage(URL.createObjectURL(blob));
    const { canvas, ctx } = createCanvas(2 * after.width, after.height);
    ctx.drawImage(before, 0, 0, after.width, after.height);
    ctx.drawImage(after, after.width, 0);
    const compareBlob = await canvasToBlob(canvas);
    const compare = new File([compareBlob], `compare.png`, {
      type: compareBlob.type,
    });
    onSave(result, compare);
    fabric.setViewportTransform(transform);
    fabric.renderAll();
  };

  const renderViewer = () => {
    return (
      <>
        <ControlsHeader
          activeStep={activeStep}
          archSettings={archSettings}
          opacity={opacity}
          filter={filter}
          onUpdateFilter={setFilter}
          onSaveMask={() => toggleCaptureMode(true)}
          onSave={handleSave}
          onReset={handleReset}
          onUpdateArchSettings={setArchSettings}
          onUpdateOpacity={setOpacity}
        />
        <div className={classes.canvasWrapper}>
          {isLoading && (
            <div className={classes.loadingWrapper}>
              <Card className={classes.loadingCard}>
                Processing simulation
                <LoopingEllipsis />
              </Card>
            </div>
          )}
          <canvas ref={canvasRef} className={classes.canvas} />
        </div>
      </>
    );
  };

  return (
    <Overlay className={classes.overlay} onClose={onClose}>
      <TitleBar onClose={onClose} />
      <div className={classes.viewerWrapper}>{renderViewer()}</div>
    </Overlay>
  );
}
